import React from 'react';
import { useUser } from '../context/UserContext';
import { Crown, Medal, User, Shield } from 'lucide-react';

const MOCK_LEADERS = [
  { name: 'Jin-su', xp: 4500, level: 45 },
  { name: 'Sarah L.', xp: 3200, level: 32 },
  { name: 'Min-ji', xp: 2800, level: 28 },
  { name: 'Alex K.', xp: 1500, level: 15 },
  { name: 'You', xp: 0, level: 0, isUser: true }, // Placeholder
];

const Leaderboard: React.FC = () => {
  const { user } = useUser();

  // Combine mock data with real user data
  const combined = MOCK_LEADERS.map(l => l.isUser ? { ...l, xp: user.xp, level: user.level } : l)
    .sort((a, b) => b.xp - a.xp);

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20">
      <header className="text-center mb-10">
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">Leaderboard</h1>
        <p className="text-nunchi-600">See who has the sharpest senses.</p>
      </header>

      <div className="bg-white rounded-2xl shadow-sm border border-nunchi-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-nunchi-50 text-nunchi-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4 font-semibold">Rank</th>
                <th className="px-6 py-4 font-semibold">Nunchi Master</th>
                <th className="px-6 py-4 font-semibold text-right">Level</th>
                <th className="px-6 py-4 font-semibold text-right">XP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-nunchi-100">
              {combined.map((leader, idx) => {
                let rankIcon = <span className="text-nunchi-400 font-mono">#{idx + 1}</span>;
                if (idx === 0) rankIcon = <Crown size={20} className="text-amber-400" fill="currentColor" />;
                if (idx === 1) rankIcon = <Medal size={20} className="text-gray-400" fill="currentColor" />;
                if (idx === 2) rankIcon = <Medal size={20} className="text-orange-400" fill="currentColor" />;

                return (
                  <tr key={idx} className={leader.isUser ? "bg-blue-50/50" : ""}>
                    <td className="px-6 py-4 flex items-center justify-center w-16">
                      {rankIcon}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${leader.isUser ? 'bg-nunchi-700 text-white' : 'bg-nunchi-100 text-nunchi-500'}`}>
                          <User size={14} />
                        </div>
                        <span className={`font-medium ${leader.isUser ? 'text-nunchi-900 font-bold' : 'text-nunchi-700'}`}>
                          {leader.name} {leader.isUser && '(You)'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-nunchi-600 font-mono">
                      {leader.level}
                    </td>
                    <td className="px-6 py-4 text-right font-bold text-nunchi-800 font-mono">
                      {leader.xp.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-4">
        <div className="bg-white p-6 rounded-xl border border-nunchi-200 text-center">
            <Shield className="mx-auto text-nunchi-400 mb-2" size={24} />
            <div className="text-2xl font-bold text-nunchi-900">{user.assessmentsCompleted}</div>
            <div className="text-xs text-nunchi-500 uppercase tracking-widest">Assessments</div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-nunchi-200 text-center">
            <User className="mx-auto text-nunchi-400 mb-2" size={24} />
            <div className="text-2xl font-bold text-nunchi-900">{user.scenariosCompleted}</div>
            <div className="text-xs text-nunchi-500 uppercase tracking-widest">Scenarios</div>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;